import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useState } from "react";
import {
    FieldValues as FieldValuesRHF,
    Resolver,
    UseFormProps as UseFormPropsRHF,
    UseFormReturn as UseFormReturnRHF,
    useForm as useFormRHF,
} from "react-hook-form";
import Yup, { ObjectSchema, ValidateOptions } from "yup";

export interface UseFormProps<FormValues extends FieldValuesRHF> {
    defaultValues?: UseFormPropsRHF<FormValues>["defaultValues"];
    options?: Omit<UseFormPropsRHF<FormValues>, "defaultValues" | "resolver">;
    schema?: ObjectSchema<FormValues, Yup.AnyObject>;
    schemaOptions?: ValidateOptions;
}

interface UseCustomFormReturn<FormValues extends FieldValuesRHF = FieldValuesRHF>
    extends UseFormReturnRHF<FormValues> {
    updateSchema: (newSchema: ObjectSchema<FormValues>) => void;
}

export function useForm<FormValues extends FieldValuesRHF>({
    defaultValues,
    options,
    schema,
    schemaOptions,
}: UseFormProps<FormValues>): UseCustomFormReturn<FormValues> {
    const [currentSchema, setCurrentSchema] = useState<ObjectSchema<FormValues> | undefined>(
        schema,
    );

    const resolver = currentSchema
        ? (yupResolver(currentSchema, schemaOptions) as unknown as Resolver<FormValues>)
        : undefined;

    const formMethods = useFormRHF<FormValues>({
        defaultValues,
        resolver,
        ...options,
    });

    const updateSchema = useCallback((newSchema: ObjectSchema<FormValues>) => {
        setCurrentSchema(newSchema);
    }, []);

    return {
        ...formMethods,
        updateSchema,
    };
}
